import { v4 as uuidV4 } from 'uuid';
import { parseAbsolute } from '@internationalized/date';
import { differenceInMilliseconds } from 'date-fns';
import {
  AirportLocationSchemaType,
  BaseAccommodationLegSchemaValues,
  BaseAviaLegSchemaValues,
  BaseCommercialAviaLegSchemaValues,
  BaseCurriServiceGroundSchemaValues,
  BaseGroundLegSchemaValues,
  BaseSelfServicedGroundSchemaValues,
  BaseSupplementalLiftAviaSchemaValues,
  BaseTmdxAffiliateGroundSchemaValues,
  BaseTmdxAviaLegSchemaValues,
  BaseTmdxDriveAffiliateGroundSchemaValues,
  BaseTmdxDriveTedsGroundSchemaValues,
  BaseTxCenterGroundSchemaValues,
  BaseTxProviderAviaSchemaValues,
  CommonRouteLegSchemaValues,
  CustomAddressSchemaType,
  DEFAULT_ACCOMMODATION_VALUES,
  DEFAULT_COMMERCIAL_AVIA_VALUES,
  DEFAULT_DEVICE_DELIVERY_VALUES,
  DEFAULT_SELF_SERVICED_VALUES,
  DEFAULT_SUPPLEMENTAL_LIFT_VALUES,
  DEFAULT_TMDX_AFFILIATE_VALUES,
  DEFAULT_TMDX_AVIA_VALUES,
  DEFAULT_TMDX_DRIVE_AFFILIATE_VALUES,
  DEFAULT_TMDX_DRIVE_TEDS_VALUES,
  DEFAULT_TX_CENTER_AVIA_VALUES,
  DEFAULT_TX_CENTER_GROUND_VALUES,
  LocationSchemaType,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  GetCurrentLegFormValues,
  getLocationData,
  getValueBySegment,
} from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import {
  AccommodationLeg,
  AviaLegTypes,
  CommercialFlightAviaLeg,
  CurriServiceGroundLeg,
  GroundLegTypes,
  LegTypeEnum,
  SegmentTypeEnum,
  SelfServicedGroundLeg,
  SupplementalLiftAviaLeg,
  TmdxAffiliateGroundLeg,
  TmdxJetAviaLeg,
  TxCenterProviderAviaLeg,
  TxCenterProviderGroundLeg,
  GroundVehicleType,
  TmdxDriveAffiliateGroundLeg,
  TmdxDriveTedsGroundLeg,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';

const getCurrentCommonLegFormValues = <T extends AviaLegTypes | GroundLegTypes>(
  leg: T,
  segment: RouteSegmentType | null,
): Omit<CommonRouteLegSchemaValues, 'departureLocation' | 'arrivalLocation' | 'formType' | 'legType'> => {
  const { minDate, maxDate } = getValueBySegment(segment);
  const departureTimeZoned = parseAbsolute(leg.departureTime, leg.departureLocalTimezone);
  const arrivalTimeZoned = parseAbsolute(leg.arrivalTime, leg.arrivalLocalTimezone);
  const timeDurationInMs = differenceInMilliseconds(leg.arrivalTime, leg.departureTime);

  return {
    id: leg.id,
    departureDateTimeValue: departureTimeZoned,
    departureDateTime: departureTimeZoned.toString(),
    departureLocalTimezone: leg.departureLocalTimezone,
    arrivalDateTimeValue: arrivalTimeZoned,
    arrivalDateTime: arrivalTimeZoned.toString(),
    arrivalLocalTimezone: leg.arrivalLocalTimezone,
    isDateTimeLinked: false,
    timeDurationInMs,
    groupId: leg.chainPosition.groupId,
    segmentType: leg.segmentType,
    comments: leg.comments || '',
    minDate: minDate || null,
    maxDate: maxDate || null,
  };
};

// AVIA LEGS
const getCommonLegAviaValues = (
  currentLeg: AviaLegTypes,
  logisticsId: string,
): Pick<BaseAviaLegSchemaValues, 'departureLocation' | 'arrivalLocation' | 'hasSCS' | 'resources' | 'formType'> => ({
  formType: LegTypeEnum.air,
  hasSCS: currentLeg.hasScs || false,
  resources: currentLeg.resources,
  departureLocation: getLocationData(
    logisticsId,
    currentLeg.legType,
    currentLeg.departureLocation,
  ) as AirportLocationSchemaType,
  arrivalLocation: getLocationData(
    logisticsId,
    currentLeg.legType,
    currentLeg.arrivalLocation,
  ) as AirportLocationSchemaType,
});

export const getCurrentTmdxAviaLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<TmdxJetAviaLeg>): BaseTmdxAviaLegSchemaValues => {
  return {
    ...DEFAULT_TMDX_AVIA_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    ...getCommonLegAviaValues(currentLeg, logisticsId),
    selectedAircraftId:
      currentLeg.resources.find((r) => r.resourceType === CaseResourceType.Aircraft)?.resourceId || '',
  };
};

export const getCurrentSupplementalLiftAviaLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<SupplementalLiftAviaLeg>): BaseSupplementalLiftAviaSchemaValues => {
  return {
    ...DEFAULT_SUPPLEMENTAL_LIFT_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    ...getCommonLegAviaValues(currentLeg, logisticsId),
    supplementalLiftProviderId: currentLeg.provider.providerId,
    crewMembers: [currentLeg.firstCrewMember, currentLeg.secondCrewMember],
    priceInCents: currentLeg.priceInCents ? `${(currentLeg.priceInCents / 100).toFixed(2)}` : '',
    tailNumber: currentLeg.tailNumber || '',
  };
};

export const getCurrentTxCenterProviderAviaLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<TxCenterProviderAviaLeg>): BaseTxProviderAviaSchemaValues => {
  return {
    ...DEFAULT_TX_CENTER_AVIA_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    ...getCommonLegAviaValues(currentLeg, logisticsId),
    txCenterProviderId: currentLeg.provider.providerId,
    crewMembers: [currentLeg.firstCrewMember, currentLeg.secondCrewMember],
    tailNumber: currentLeg.tailNumber || '',
  };
};

export const getCurrentCommercialFlightAviaLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<CommercialFlightAviaLeg>): BaseCommercialAviaLegSchemaValues => {
  return {
    ...DEFAULT_COMMERCIAL_AVIA_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    ...getCommonLegAviaValues(currentLeg, logisticsId),
    flightNumber: currentLeg.flightNumber,
    priceInCents: currentLeg.priceInCents ? `${currentLeg.priceInCents / 100}` : '',
    bookingNumber: currentLeg.bookingNumber || '',
    sabreGroupId: currentLeg.sabreGroupId || '',
    seatType: currentLeg.seatType || '',
    flightOperator: currentLeg.flightOperator || '',
    numberOfSeats: currentLeg.numberOfSeats || 1,
  } as BaseCommercialAviaLegSchemaValues;
};

// GROUND LEGS
export const getCommonGroundLegValues = ({
  resources,
  driver,
  email,
  phoneNumber,
  plateNumber,
  vehicleType,
  reservationNumber,
  pickup,
  dropoff,
  wait,
  stay,
  distanceInMeters,
  isFeasibleForDrive,
  hasScs,
}: TmdxAffiliateGroundLeg | TxCenterProviderGroundLeg): Pick<
  BaseGroundLegSchemaValues,
  | 'formType'
  | 'driver'
  | 'email'
  | 'phoneNumber'
  | 'plateNumber'
  | 'vehicleType'
  | 'reservationNumber'
  | 'pickup'
  | 'dropoff'
  | 'wait'
  | 'stay'
  | 'distanceInMeters'
  | 'isFeasibleForDrive'
  | 'resources'
  | 'hasSCS'
> => ({
  formType: LegTypeEnum.ground,
  driver: driver || '',
  email: email || '',
  phoneNumber,
  plateNumber: plateNumber || '',
  vehicleType: vehicleType || GroundVehicleType.SUV,
  reservationNumber: reservationNumber || '',
  pickup: pickup || '',
  dropoff: dropoff || '',
  wait: wait || '',
  stay: stay || '',
  distanceInMeters,
  isFeasibleForDrive,
  resources,
  hasSCS: hasScs || false,
});

export const getCurrentTmdxAffiliateGroundLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<TmdxAffiliateGroundLeg>): BaseTmdxAffiliateGroundSchemaValues => {
  return {
    ...DEFAULT_TMDX_AFFILIATE_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    ...getCommonGroundLegValues(currentLeg),
    departureLocation: getLocationData(
      logisticsId,
      currentLeg.legType,
      currentLeg.departureLocation,
    ) as LocationSchemaType,
    arrivalLocation: getLocationData(logisticsId, currentLeg.legType, currentLeg.arrivalLocation) as LocationSchemaType,
  } as BaseTmdxAffiliateGroundSchemaValues;
};

export const getCurrentTmdxDriveAffiliateGroundLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<TmdxDriveAffiliateGroundLeg>): BaseTmdxDriveAffiliateGroundSchemaValues => {
  return {
    ...DEFAULT_TMDX_DRIVE_AFFILIATE_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    distanceInMeters: currentLeg.distanceInMeters,
    isFeasibleForDrive: currentLeg.isFeasibleForDrive,
    resources: currentLeg.resources,
    hasSCS: currentLeg.hasScs || false,
    departureLocation: getLocationData(
      logisticsId,
      currentLeg.legType,
      currentLeg.departureLocation,
    ) as LocationSchemaType,
    arrivalLocation: getLocationData(logisticsId, currentLeg.legType, currentLeg.arrivalLocation) as LocationSchemaType,
    tripId: currentLeg.tripReference?.tripId || null,
  } as BaseTmdxDriveAffiliateGroundSchemaValues;
};

export const getCurrentTmdxDriveTedsGroundLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<TmdxDriveTedsGroundLeg>): BaseTmdxDriveTedsGroundSchemaValues => {
  return {
    ...DEFAULT_TMDX_DRIVE_TEDS_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    distanceInMeters: currentLeg.distanceInMeters,
    isFeasibleForDrive: currentLeg.isFeasibleForDrive,
    resources: currentLeg.resources,
    hasSCS: currentLeg.hasScs || false,
    departureLocation: getLocationData(
      logisticsId,
      currentLeg.legType,
      currentLeg.departureLocation,
    ) as LocationSchemaType,
    arrivalLocation: getLocationData(logisticsId, currentLeg.legType, currentLeg.arrivalLocation) as LocationSchemaType,
    tripId: currentLeg.tripReference?.tripId || null,
  } as BaseTmdxDriveTedsGroundSchemaValues;
};

export const getCurrentSelfServicedGroundLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<SelfServicedGroundLeg>): BaseSelfServicedGroundSchemaValues => {
  return {
    ...DEFAULT_SELF_SERVICED_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    distanceInMeters: currentLeg.distanceInMeters,
    isFeasibleForDrive: currentLeg.isFeasibleForDrive,
    resources: currentLeg.resources,
    hasSCS: currentLeg.hasScs || false,
    departureLocation: getLocationData(
      logisticsId,
      currentLeg.legType,
      currentLeg.departureLocation,
    ) as LocationSchemaType,
    arrivalLocation: getLocationData(logisticsId, currentLeg.legType, currentLeg.arrivalLocation) as LocationSchemaType,
  } as BaseSelfServicedGroundSchemaValues;
};

export const getCurrentTxCenterProviderGroundLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<TxCenterProviderGroundLeg>): BaseTxCenterGroundSchemaValues => {
  return {
    ...DEFAULT_TX_CENTER_GROUND_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    ...getCommonGroundLegValues(currentLeg),
    departureLocation: getLocationData(
      logisticsId,
      currentLeg.legType,
      currentLeg.departureLocation,
    ) as LocationSchemaType,
    arrivalLocation: getLocationData(logisticsId, currentLeg.legType, currentLeg.arrivalLocation) as LocationSchemaType,
    txCenterProviderId: currentLeg.provider.providerId,
  } as BaseTxCenterGroundSchemaValues;
};

export const getCurrentCurriServiceGroundLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<CurriServiceGroundLeg>): BaseCurriServiceGroundSchemaValues => {
  return {
    ...DEFAULT_DEVICE_DELIVERY_VALUES,
    ...getCurrentCommonLegFormValues(currentLeg, segment),
    resources: currentLeg.resources,
    departureLocation: getLocationData(
      logisticsId,
      currentLeg.legType,
      currentLeg.departureLocation,
    ) as LocationSchemaType,
    arrivalLocation: getLocationData(logisticsId, currentLeg.legType, currentLeg.arrivalLocation) as LocationSchemaType,
    trackingNumber: currentLeg.trackingNumber,
    hasSCS: currentLeg.hasScs || false,
  } as BaseCurriServiceGroundSchemaValues;
};

export const getCurrentAccommodationLegFormValues = ({
  currentLeg,
  segment,
  logisticsId,
}: GetCurrentLegFormValues<AccommodationLeg>): BaseAccommodationLegSchemaValues => {
  const location = getLocationData(logisticsId, currentLeg.legType, currentLeg.location) as CustomAddressSchemaType;
  const checkInAtZoned = parseAbsolute(currentLeg.checkInAt, location.timezone);
  const checkOutAtZoned = parseAbsolute(currentLeg.checkOutAt, location.timezone);

  return {
    ...DEFAULT_ACCOMMODATION_VALUES,
    id: currentLeg.id,
    arrivalLocation: location,
    segmentType: SegmentTypeEnum.custom,
    groupId: segment?.id || uuidV4(),
    checkInAtValue: checkInAtZoned,
    checkInAt: checkInAtZoned.toString(),
    checkOutAtValue: checkOutAtZoned,
    checkOutAt: checkOutAtZoned.toString(),
    hotelName: currentLeg.hotelName,
    rooms: currentLeg.rooms,
    bookingNumber: currentLeg.bookingNumber || '',
    resources: currentLeg.resources,
    comments: currentLeg.comments || '',
    hasScs: currentLeg.hasScs || false,
  };
};
